import { useEffect, useState } from "react";
import { useSearchParams, Navigate, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { useAcceptPartnerInvitation } from "@/hooks/use-partner-invitations";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Loader2, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";
import { UrlTemplateService } from "@/services/urlTemplateService";

// Branding via URL template (partner_invitation)
const usePartnerTemplate = () => {
  const [template, setTemplate] = useState<any>(null);
  const [organizationName, setOrganizationName] = useState<string>('Snapshot Live');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from('url_templates')
          .select('*')
          .eq('template_type', 'partner_invitation')
          .eq('is_active', true)
          .maybeSingle();
        setTemplate(data || null);
        
        // Get organization name from the default organization
        if (data) {
          const { data: orgData } = await supabase
            .from('organizations')
            .select('name')
            .eq('id', '00000000-0000-0000-0000-000000000000')
            .maybeSingle();
          
          if (orgData?.name) {
            setOrganizationName(orgData.name);
          }
        }
      } catch (e) {
        setTemplate(null);
      }
    })();
  }, []);

  const process = (text?: string) => {
    if (!text) return '';
    return text.replace(/\{\{organizationName\}\}/g, organizationName);
  };

  const buildInviteUrl = ({ token, partnerId }: { token: string; partnerId?: string }) => {
    if (!template) {
      // New unified onboarding flow
      const origin = window.location.origin;
      return partnerId 
        ? `${origin}/onboarding?token=${token}&partner_id=${partnerId}`
        : `${origin}/onboarding?token=${token}`;
    }

    try {
      const baseUrl = template.content_config?.base_url || window.location.origin.replace(/\/$/, '');
      const templateUrl = template.url_template || '/onboarding';
      
      const variables = {
        baseUrl,
        invitationToken: token,
        partnerId: partnerId || ''
      };

      const processedUrl = UrlTemplateService.processUrlTemplate(templateUrl, variables);
      
      // Ensure absolute URL for Supabase emailRedirectTo
      const finalUrl = processedUrl.startsWith('http') 
        ? processedUrl 
        : `${baseUrl}${processedUrl.startsWith('/') ? '' : '/'}${processedUrl}`;

      console.debug('Partner invite redirect URL (unified):', finalUrl);
      return finalUrl;
    } catch (error) {
      console.warn('Failed to build invite URL from template:', error);
      // Fallback to unified onboarding flow
      const origin = window.location.origin;
      return partnerId 
        ? `${origin}/onboarding?token=${token}&partner_id=${partnerId}`
        : `${origin}/onboarding?token=${token}`;
    }
  };

  const getStyles = () => {
    if (!template) return {} as any;
    const styles: any = {};
    const colors = template.color_config || {};
    const fonts = template.font_config || {};
    
    if (colors.background_color) {
      // Convert HSL string to CSS format
      const bgColor = colors.background_color.includes('hsl') 
        ? colors.background_color 
        : `hsl(${colors.background_color})`;
      styles.backgroundColor = bgColor;
    }
    
    if (colors.text_color) {
      const textColor = colors.text_color.includes('hsl') 
        ? colors.text_color 
        : `hsl(${colors.text_color})`;
      styles['--text-color'] = textColor;
      styles.color = textColor;
    }
    
    if (colors.primary_color) {
      const primaryColor = colors.primary_color.includes('hsl') 
        ? colors.primary_color 
        : `hsl(${colors.primary_color})`;
      styles['--primary-color'] = primaryColor;
    }
    
    if (fonts.font_family) {
      styles.fontFamily = fonts.font_family;
    }
    
    return styles;
  };

  const content = () => {
    const c = template?.content_config || {};
    return {
      logo_url: c.logo_url || null,
      heading: process(c.heading) || process(c.page_title) || 'Welcome to Snapshot Live',
      subheading: process(c.description) || 'Partner Portal Invitation',
    };
  };

  return { template, getStyles, content, buildInviteUrl };
};

const PartnerInviteAccept = () => {
  // Immediately redirect ALL partner invitations to unified onboarding flow
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const partnerId = searchParams.get("partner_id");
  const partnerName = searchParams.get("partner_name");
  
  useEffect(() => {
    if (token) {
      console.log('🔄 Redirecting partner invitation to unified onboarding flow');
      
      // Build redirect URL with all parameters
      const params = new URLSearchParams();
      params.set('token', token);
      if (partnerId) params.set('partner_id', partnerId);
      if (partnerName) params.set('partner_name', partnerName);
      
      const redirectUrl = `/onboarding?${params.toString()}`;
      
      // Use replace to avoid flash and prevent back navigation issues
      window.location.replace(redirectUrl);
      return;
    }
  }, [token, partnerId, partnerName]);

  // Show loading while redirecting
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Redirecting...</p>
      </div>
    </div>
  );
};

const LegacyPartnerInviteAccept = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [authError, setAuthError] = useState("");
  const token = searchParams.get("token");
  const partnerId = searchParams.get("partner_id");
  
  const acceptInvitation = useAcceptPartnerInvitation();
  
  const { template, getStyles, content, buildInviteUrl } = usePartnerTemplate();

  // Password validation
  const validatePassword = (pwd: string) => {
    const requirements = {
      minLength: pwd.length >= 10,
      hasLowercase: /[a-z]/.test(pwd),
      hasUppercase: /[A-Z]/.test(pwd),
      hasNumber: /\d/.test(pwd),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
      isPassphrase: pwd.length >= 16
    };

    const score = Object.values(requirements).filter(Boolean).length;
    const isStrong = requirements.minLength && 
      (requirements.isPassphrase || 
       (requirements.hasLowercase && requirements.hasUppercase && 
        requirements.hasNumber && requirements.hasSpecial));

    return { requirements, score, isStrong };
  };

  const passwordValidation = validatePassword(password);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const canSubmit = isSignUp ? (passwordValidation.isStrong && passwordsMatch) : password.length > 0;

  // Fetch invitation details using direct database queries
  const { data: invitation, isLoading, error } = useQuery({
    queryKey: ["partner-invitation", token],
    queryFn: async () => {
      if (!token) throw new Error("No invitation token provided");
      
      // Check user_invitations first
      const { data: userInvite, error: userError } = await supabase
        .from('user_invitations')
        .select('id, email, status, expires_at, created_at, invited_role')
        .eq('id', token)
        .maybeSingle();

      if (userError) {
        console.error('Error checking user_invitations:', userError);
        throw new Error('Failed to fetch invitation');
      }

      if (!userInvite) {
        throw new Error("Invitation not found");
      }

      // Validate invitation
      if (userInvite.status !== 'pending') {
        throw new Error("This invitation has already been processed");
      }
      if (new Date(userInvite.expires_at) <= new Date()) {
        throw new Error("This invitation has expired");
      }
      if (userInvite.invited_role !== 'partner') {
        throw new Error("This is not a partner invitation");
      }

      // Get partner details if partner_id provided
      let partner = null;
      if (partnerId) {
        const { data: partnerData } = await supabase
          .from('crm_partners')
          .select('id, first_name, last_name, email')
          .eq('id', partnerId)
          .maybeSingle();
        partner = partnerData;
      }

      return {
        id: userInvite.id,
        partner_id: partnerId,
        email: userInvite.email,
        invitation_token: token,
        status: userInvite.status,
        expires_at: userInvite.expires_at,
        created_at: userInvite.created_at,
        crm_partners: partner
      };
    },
    enabled: !!token,
    retry: 1
  });

  useEffect(() => {
    if (invitation) {
      setEmail(invitation.email);
    }
  }, [invitation]);

  // If user is already logged in and invitation exists, accept it
  useEffect(() => {
    if (session?.user && invitation && !acceptInvitation.isSuccess) {
      acceptInvitation.mutate({
        token: token!,
        userId: session.user.id
      });
    }
  }, [session, invitation, token]);

  // Redirect after successful acceptance
  useEffect(() => {
    if (acceptInvitation.isSuccess) {
      // Clear pending invitation from session storage
      try {
        sessionStorage.removeItem('pendingInvitation');
      } catch (e) {
        console.warn('Could not clear sessionStorage:', e);
      }
      navigate("/dashboard/partner-portal");
    }
  }, [acceptInvitation.isSuccess, navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError("");

    try {
      if (isSignUp) {
        // Save pending invitation to sessionStorage before signup
        const pendingInvitation = {
          token,
          invitedRole: 'partner',
          partnerId
        };
        sessionStorage.setItem("pendingInvitation", JSON.stringify(pendingInvitation));

        const redirectUrl = buildInviteUrl({ token: token!, partnerId: partnerId || undefined });

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              invitation_token: token,
              partner_id: partnerId,
              invited_role: 'partner'
            }
          }
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) throw error;
      }
    } catch (error: any) {
      setAuthError(error.message);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-slate-600">No invitation token was provided.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !invitation) {
    const errorMessage = error?.message || "Unknown error";
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Invitation Error</h2>
            <p className="text-slate-600 mb-4">
              {errorMessage}
            </p>
            {errorMessage.includes("contact support") && (
              <p className="text-sm text-slate-500">
                If this problem persists, please reach out to our support team.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (session?.user && acceptInvitation.isSuccess) {
    return <Navigate to="/dashboard/partner-portal" replace />;
  }

  if (session?.user && acceptInvitation.isPending) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Accepting Invitation</h2>
            <p className="text-slate-600">Please wait while we set up your partner access...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const templateContent = content();
  const templateStyles = getStyles();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" style={templateStyles}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {templateContent.logo_url && (
            <div className="mb-4">
              <img 
                src={templateContent.logo_url} 
                alt="Company Logo" 
                className="h-12 mx-auto"
              />
            </div>
          )}
          {!templateContent.logo_url && (
            <div className="bg-teal-100 p-3 rounded-full w-fit mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-teal-600" />
            </div>
          )}
          <CardTitle className="text-xl" style={{ color: templateStyles['--text-color'] || 'inherit' }}>
            {templateContent.heading}
          </CardTitle>
          <p className="text-slate-600" style={{ color: templateStyles['--text-color'] ? `${templateStyles['--text-color']}aa` : 'inherit' }}>
            {templateContent.subheading}
            {invitation.crm_partners && (
              <span className="block mt-1">
                {invitation.crm_partners.first_name} {invitation.crm_partners.last_name}
              </span>
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled
                className="bg-slate-50"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder={isSignUp ? "Create a strong password" : "Enter your password"}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff size={16} />
                  ) : (
                    <Eye size={16} />
                  )}
                </button>
              </div>
              
              {isSignUp && password && (
                <div className="mt-3 p-3 bg-muted rounded-lg">
                  <div className="text-xs font-medium mb-2 text-muted-foreground">Password Requirements:</div>
                  <div className="space-y-1">
                    <div className={`flex items-center text-xs ${passwordValidation.requirements.minLength ? 'text-green-600' : 'text-muted-foreground'}`}>
                      <span className="mr-2">{passwordValidation.requirements.minLength ? '✓' : '○'}</span>
                      At least 10 characters
                    </div>
                    <div className={`flex items-center text-xs ${passwordValidation.requirements.hasLowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                      <span className="mr-2">{passwordValidation.requirements.hasLowercase ? '✓' : '○'}</span>
                      One lowercase letter (a-z)
                    </div>
                    <div className={`flex items-center text-xs ${passwordValidation.requirements.hasUppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                      <span className="mr-2">{passwordValidation.requirements.hasUppercase ? '✓' : '○'}</span>
                      One uppercase letter (A-Z)
                    </div>
                    <div className={`flex items-center text-xs ${passwordValidation.requirements.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                      <span className="mr-2">{passwordValidation.requirements.hasNumber ? '✓' : '○'}</span>
                      One number (0-9)
                    </div>
                    <div className={`flex items-center text-xs ${passwordValidation.requirements.hasSpecial ? 'text-green-600' : 'text-muted-foreground'}`}>
                      <span className="mr-2">{passwordValidation.requirements.hasSpecial ? '✓' : '○'}</span>
                      One special character (!@#$%^&*)
                    </div>
                    {passwordValidation.requirements.isPassphrase && (
                      <div className="flex items-center text-xs text-blue-600">
                        <span className="mr-2">✓</span>
                        Strong passphrase (16+ characters)
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            passwordValidation.score <= 2 ? 'bg-red-500' : 
                            passwordValidation.score <= 4 ? 'bg-yellow-500' : 
                            'bg-green-500'
                          }`}
                          style={{ width: `${(passwordValidation.score / 6) * 100}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${
                        passwordValidation.score <= 2 ? 'text-red-600' : 
                        passwordValidation.score <= 4 ? 'text-yellow-600' : 
                        'text-green-600'
                      }`}>
                        {passwordValidation.isStrong ? 'Strong' : 
                         passwordValidation.score <= 2 ? 'Weak' : 'Fair'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {isSignUp && (
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Confirm your password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </button>
                </div>
                {confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                )}
                {confirmPassword && passwordsMatch && (
                  <p className="text-xs text-green-600 mt-1">✓ Passwords match</p>
                )}
              </div>
            )}
            
            {authError && (
              <Alert variant="destructive">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={!canSubmit}>
              {isSignUp ? "Create Account & Accept" : "Sign In & Accept"}
            </Button>

            {isSignUp && !canSubmit && (
              <p className="text-xs text-muted-foreground text-center">
                Please ensure your password meets all requirements and both password fields match.
              </p>
            )}
          </form>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-sm text-teal-600 hover:underline"
            >
              {isSignUp 
                ? "Already have an account? Sign in" 
                : "Don't have an account? Create one"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerInviteAccept;